import { VideoContent } from '../../../types/content';
import { CONTENT_TYPES } from '../../../types/lives';
import { env } from '../../../config/env';
import { fetchWithErrorHandling } from '../../../utils/api';

interface YouTubeVideoResponse {
  items: Array<{
    id: {
      videoId: string;
    };
    snippet: {
      title: string;
      description: string;
      thumbnails: {
        high: {
          url: string;
        };
      };
      channelTitle: string;
      publishedAt: string;
    };
    liveStreamingDetails?: {
      actualStartTime?: string;
      actualEndTime?: string;
      scheduledStartTime?: string;
      scheduledEndTime?: string;
      concurrentViewers?: string;
    };
  }>;
}

export class YouTubeProvider {
  private readonly apiKey: string | null;
  private readonly channelId: string | null;
  private readonly isEnabled: boolean;

  constructor() {
    this.apiKey = env.providers.youtube.apiKey || null;
    this.channelId = env.providers.youtube.channelId || null;
    this.isEnabled = env.providers.youtube.enabled && Boolean(this.apiKey) && Boolean(this.channelId);
    
    if (!this.isEnabled) {
      console.warn('YouTube provider is not properly configured. Live video features will be disabled.');
    }
  }

  private async makeRequest<T>(url: string, params: Record<string, string>): Promise<T> {
    if (!this.isEnabled) {
      throw new Error('YouTube provider is not properly configured');
    }

    // Add API key to params
    const paramsWithKey = {
      ...params,
      key: this.apiKey!
    };

    try {
      // Use the centralized fetch wrapper with a full URL (not just endpoint)
      return await fetchWithErrorHandling<T>({
        baseUrl: '', // Empty because we're providing the full URL
        endpoint: url, // This is the full URL for YouTube API
        params: paramsWithKey,
        validateApiResponse: false, // YouTube API has a different response format
        headers: {
          'Accept': 'application/json'
        }
      });
    } catch (error) {
      // Re-throw the error - it's already been logged by the fetch wrapper
      throw error;
    }
  }

  public async getLiveVideos(): Promise<VideoContent | null> {
    if (!this.isEnabled) {
      console.warn('YouTube provider is not properly configured. Returning null for live videos.');
      return null;
    }

    try {
      // First, search for live broadcasts
      const searchResponse = await this.makeRequest<YouTubeVideoResponse>(
        'https://www.googleapis.com/youtube/v3/search',
        {
          part: 'id,snippet',
          channelId: this.channelId!,
          eventType: 'live',
          type: 'video',
          maxResults: '1',
        }
      );

      if (!searchResponse.items?.length) {
        // If no live streams, check for upcoming
        const upcomingResponse = await this.makeRequest<YouTubeVideoResponse>(
          'https://www.googleapis.com/youtube/v3/search',
          {
            part: 'id,snippet',
            channelId: this.channelId!,
            eventType: 'upcoming',
            type: 'video',
            maxResults: '1',
          }
        );

        if (!upcomingResponse.items?.length) {
          return null;
        }

        const video = upcomingResponse.items[0];
        // Get additional video details including live streaming details
        const videoResponse = await this.makeRequest<YouTubeVideoResponse>(
          'https://www.googleapis.com/youtube/v3/videos',
          {
            part: 'snippet,liveStreamingDetails',
            id: video.id.videoId,
          }
        );

        if (!videoResponse.items?.length) {
          return null;
        }

        const videoDetails = videoResponse.items[0];
        return this.mapVideoResponse(videoDetails, 'upcoming');
      }

      const video = searchResponse.items[0];
      // Get additional video details for live stream
      const videoResponse = await this.makeRequest<YouTubeVideoResponse>(
        'https://www.googleapis.com/youtube/v3/videos',
        {
          part: 'snippet,liveStreamingDetails',
          id: video.id.videoId,
        }
      );

      if (!videoResponse.items?.length) {
        return null;
      }

      const videoDetails = videoResponse.items[0];
      return this.mapVideoResponse(videoDetails, 'live');
    } catch (error) {
      console.error('Failed to fetch YouTube live videos:', error);
      return null;
    }
  }

  private mapVideoResponse(videoDetails: YouTubeVideoResponse['items'][0], status: 'live' | 'upcoming'): VideoContent {
    const liveDetails = videoDetails.liveStreamingDetails;

    return {
      id: videoDetails.id.videoId,
      type: CONTENT_TYPES.LIVE,
      title: videoDetails.snippet.title,
      description: videoDetails.snippet.description,
      youtube: videoDetails.id.videoId,
      thumbnail: videoDetails.snippet.thumbnails.high.url,
      broadcastStatus: status,
      scheduledStartTime: liveDetails?.scheduledStartTime,
      scheduledEndTime: liveDetails?.scheduledEndTime,
      actualStartTime: liveDetails?.actualStartTime,
      actualEndTime: liveDetails?.actualEndTime,
      organization: this.channelId!,
      createdAt: videoDetails.snippet.publishedAt,
      updatedAt: videoDetails.snippet.publishedAt,
      duration: 0,
      language: 'en',
      tags: [],
      topics: [],
      mimeType: 'video/youtube',
      parent: null,
      uploadState: 'completed',
      createdBy: videoDetails.snippet.channelTitle,
      url: '',
    };
  }
}